import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useFeatures } from '../lib/features';
import { ROLE_LABELS } from '../mock';
import { useProviders, usePayers, useAutomationRules, useTeam } from '../api/hooks';
import { relTime, initials } from '../lib/data';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { CheckCircle2, Bot, Zap, Plug, FileWarning, ShieldCheck, Calculator, Plus } from 'lucide-react';
import { toast } from '../lib/aiToast';
import AddProvider from '../components/AddProvider';

export default function Settings() {
  const { session } = useAuth();
  const { features, toggle: toggleFeature } = useFeatures();
  const practiceId = session?.practice?.id || 'prac_001';
  const { data: users = [] } = useTeam(practiceId);
  const { data: providers = [] } = useProviders(practiceId);
  const { data: payers = [] } = usePayers();
  const { data: automationRules = [] } = useAutomationRules(practiceId);

  // Automation rules have no update endpoint in v1; mirror the fetched rules into
  // local state so toggling mode/enabled stays interactive for the demo.
  const [rules, setRules] = useState([]);
  useEffect(() => { setRules(automationRules.map((r) => ({ ...r }))); }, [automationRules]);
  const [addProviderOpen, setAddProviderOpen] = useState(false);

  const toggle = (id) => setRules((rs) => rs.map((r) => {
    if (r.id !== id) return r;
    const enabled = !r.enabled;
    toast.message(`${r.name} ${enabled ? 'enabled' : 'paused'}`);
    return { ...r, enabled };
  }));
  const setMode = (id, mode) => setRules((rs) => rs.map((r) => (r.id === id ? { ...r, mode } : r)));

  return (
    <div className="space-y-5 max-w-5xl">
      <PageHeader eyebrow="System" title="Settings" subtitle="Practice profile, automation rules, providers, payers, and integrations." />

      {/* Feature modules — what the product surfaces */}
      <div className="dh-card p-5">
        <div className="text-sm font-semibold text-slate-900">Feature modules</div>
        <div className="text-xs text-slate-500 mt-0.5 mb-4">Turn product context on or off across the whole app. V1 ships eligibility-first.</div>
        <div className="space-y-3">
          <div className="flex items-start gap-3.5 rounded-xl border border-slate-200 p-3.5">
            <div className="h-9 w-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 flex items-center gap-2">Eligibility &amp; patient responsibility <span className="pill pill-green"><CheckCircle2 className="h-3 w-3" />Core</span></div>
              <div className="text-xs text-slate-500 mt-0.5">270/271 verification, risk triage, workflows. Always on.</div>
            </div>
            <Switch checked disabled className="mt-1" />
          </div>
          <div className="flex items-start gap-3.5 rounded-xl border border-slate-200 p-3.5">
            <div className="h-9 w-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Calculator className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">Good-faith estimator</div>
              <div className="text-xs text-slate-500 mt-0.5">Patient-responsibility estimates page. Deferred for the pilot demo — off by default.</div>
            </div>
            <Switch
              checked={features.estimator}
              onCheckedChange={() => { toggleFeature('estimator'); toast.message(`Estimator ${features.estimator ? 'hidden' : 'enabled'}`); }}
              className="mt-1"
            />
          </div>
          <div className="flex items-start gap-3.5 rounded-xl border border-slate-200 p-3.5">
            <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">Prior authorization</div>
              <div className="text-xs text-slate-500 mt-0.5">Authorization workflow queue + prior-auth packets. Deferred for the pilot demo — off by default.</div>
            </div>
            <Switch
              checked={features.priorAuth}
              onCheckedChange={() => { toggleFeature('priorAuth'); toast.message(`Prior authorization ${features.priorAuth ? 'hidden' : 'enabled'}`); }}
              className="mt-1"
            />
          </div>
          <div className="flex items-start gap-3.5 rounded-xl border border-slate-200 p-3.5">
            <div className="h-9 w-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><FileWarning className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">Claims &amp; Denials</div>
              <div className="text-xs text-slate-500 mt-0.5">Retrospective RCM: recurring-denial patterns in Insights, claims-history flags on verifications, and copilot denial queries. Off by default for the paid-first V1.</div>
            </div>
            <Switch
              checked={features.claimsDenials}
              onCheckedChange={() => { toggleFeature('claimsDenials'); toast.message(`Claims & Denials ${features.claimsDenials ? 'hidden' : 'enabled'}`); }}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      <Tabs defaultValue="automation">
        <TabsList>
          <TabsTrigger value="automation">Automation</TabsTrigger>
          <TabsTrigger value="practice">Practice</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="users">Users & roles</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
        </TabsList>

        {/* Automation rules — the configurable layer over Stedi */}
        <TabsContent value="automation" className="mt-4 space-y-3">
          <div className="ai-surface p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-violet-600 text-white flex items-center justify-center shrink-0"><Bot className="h-5 w-5" /></div>
            <div className="text-sm text-slate-700">Rules decide what agents do on their own. <b>Autopilot</b> acts automatically; <b>Copilot</b> drafts and waits for human approval.</div>
          </div>
          {rules.map((r) => (
            <div key={r.id} className="dh-card p-4 flex items-start gap-4">
              <div className="pt-0.5"><Switch checked={r.enabled} onCheckedChange={() => toggle(r.id)} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-900">{r.name}</div>
                  <span className={`pill ${r.mode === 'autopilot' ? 'pill-green' : 'pill-indigo'}`}><Zap className="h-3 w-3" />{r.mode === 'autopilot' ? 'Autopilot' : 'Copilot'}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{r.description}</div>
                <div className="text-[11px] text-slate-400 mt-1.5 tnum">{r.runsThisWeek} runs this week{r.lastRun ? ` · last ${relTime(r.lastRun)}` : ' · never run'}</div>
              </div>
              <div className="shrink-0">
                <div className="seg">
                  <button className={r.mode === 'copilot' ? 'active' : ''} onClick={() => setMode(r.id, 'copilot')} disabled={!r.enabled}>Copilot</button>
                  <button className={r.mode === 'autopilot' ? 'active' : ''} onClick={() => setMode(r.id, 'autopilot')} disabled={!r.enabled}>Autopilot</button>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="practice" className="mt-4">
          <div className="dh-card p-5 max-w-2xl">
            <div className="text-sm font-semibold text-slate-900 mb-4">Practice profile</div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Practice name</Label><Input className="mt-1.5" defaultValue={session?.practice?.name} /></div>
              <div><Label>NPI</Label><Input className="mt-1.5 tnum" defaultValue={session?.practice?.npi} /></div>
              <div><Label>Tax ID</Label><Input className="mt-1.5 tnum" defaultValue={session?.practice?.taxId} /></div>
              <div><Label>Specialty</Label><Input className="mt-1.5" defaultValue={session?.practice?.specialty} /></div>
              <div className="col-span-2"><Label>Address</Label><Input className="mt-1.5" defaultValue={session?.practice?.address} /></div>
            </div>
            <Button className="mt-5 bg-brand-600 hover:bg-brand-700 text-white" onClick={() => toast.success('Saved')}>Save changes</Button>
          </div>
        </TabsContent>

        <TabsContent value="providers" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-slate-500">{providers.length} provider{providers.length === 1 ? '' : 's'} · NPIs sign 270 eligibility requests.</div>
            <Button size="sm" className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => setAddProviderOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />Add provider
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {providers.map((p) => (
              <div key={p.id} className="dh-card p-5">
                <div className="font-semibold text-slate-900">Dr. {p.firstName} {p.lastName}</div>
                <div className="mt-2 text-sm text-slate-600">NPI <span className="mono">{p.npi}</span></div>
                <div className="text-sm text-slate-600">Taxonomy {p.taxonomyCode} · {p.licenseState}</div>
              </div>
            ))}
          </div>
          <AddProvider open={addProviderOpen} onClose={() => setAddProviderOpen(false)} practiceId={practiceId} />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="dh-card-flush overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 bg-slate-50/70"><tr><th className="text-left px-4 py-2.5 font-medium">User</th><th className="text-left px-4 py-2.5 font-medium">Email</th><th className="text-left px-4 py-2.5 font-medium">Role</th><th className="text-left px-4 py-2.5 font-medium">Last login</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Avatar className="h-8 w-8"><AvatarFallback className="bg-brand-100 text-brand-700 text-xs">{initials(u.name)}</AvatarFallback></Avatar><span className="font-medium text-slate-900">{u.name}</span></div></td>
                    <td className="px-4 py-3 text-slate-700">{u.email}</td>
                    <td className="px-4 py-3"><span className="pill pill-indigo">{ROLE_LABELS[u.role]}</span></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{u.lastLogin ? relTime(u.lastLogin) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="dh-card p-5">
              <div className="flex items-center justify-between">
                <div><div className="font-semibold text-slate-900">Stedi clearinghouse</div><div className="text-xs text-slate-500">270/271 · 837 · 835 · 276/277</div></div>
                <span className="pill pill-green"><CheckCircle2 className="h-3 w-3" />Sandbox</span>
              </div>
              <div className="mt-4"><Label>Stedi API key</Label><Input className="mt-1.5 mono" placeholder="sk_test_… (masked, server-side)" /></div>
              <Button className="mt-4" variant="outline" onClick={() => toast.success('Connection OK')}>Test connection</Button>
            </div>
            <div className="dh-card p-5 ring-1 ring-brand-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-2">Athena Health (PM)<span className="pill pill-indigo">Primary</span></div>
                  <div className="text-xs text-slate-500">Embeds via Athena's exposed APIs · also runs standalone</div>
                </div>
                <span className="pill pill-amber"><Plug className="h-3 w-3" />Connect</span>
              </div>
              <div className="mt-4 text-sm text-slate-700">First-release deployment target. Runs <b>embedded inside Athena</b> for a high-impact in-workflow experience, or as a <b>standalone</b> dashboard. Lets the Data Sync Agent push payer-verified corrections back to the PM record.</div>
              <Button className="mt-4 bg-brand-600 hover:bg-brand-700 text-white">Connect Athena</Button>
            </div>
            <div className="dh-card p-5">
              <div className="flex items-center justify-between">
                <div><div className="font-semibold text-slate-900">AI agents</div><div className="text-xs text-slate-500">271 parsing · risk · comms · retrospective</div></div>
                <span className="pill pill-violet"><Bot className="h-3 w-3" />7 active</span>
              </div>
              <div className="mt-4 text-sm text-slate-700">All agent outputs are confidence-scored and link back to source data for audit.</div>
            </div>
            <div className="dh-card p-5">
              <div className="font-semibold text-slate-900">Configured payers</div>
              <div className="mt-3 flex flex-wrap gap-1.5">{payers.map((p) => <span key={p.id} className="pill pill-slate">{p.name}</span>)}</div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
